import React, { Component, Fragment, ReactNode } from 'react';
import FormattedProductPrice from '~/FormattedProductPrice';
import { Item as ProductItem, Product } from '~/Api/Product';
import PricedProduct from '~/Product/PricedProduct';
import ProductPriceInfo from '~/ProductPriceInfo';
import ProductStatusIcon from '~/ProductStatusIcon';
import { Link } from '~/UI/Button';
import Checkbox from '~/UI/Checkbox/Checkbox';
import CountryIcon from '~/UI/CountryIcon/CountryIcon';

import styles from './styles.less';

interface Props {
	index: number;
	pricedProduct: PricedProduct<ProductItem>;
	highlightProductId: string | null;
	priceType: string | null;
	accessory?(product: PricedProduct<ProductItem>): ReactNode;
	detailLink(product: Product): { pathname: string; search: string };
	packageCell?(product: PricedProduct<ProductItem>, index: number): ReactNode;
	isFetching: boolean;
	style: any;
	checked: boolean;
	checkRow(value: boolean): void;
}

export default class Row extends Component<Props> {
	shouldComponentUpdate(nextProps: Props) {
		return (
			this.props.index !== nextProps.index ||
			this.props.pricedProduct !== nextProps.pricedProduct ||
			this.props.highlightProductId !== nextProps.highlightProductId ||
			this.props.priceType !== nextProps.priceType ||
			this.props.accessory !== nextProps.accessory ||
			this.props.detailLink !== nextProps.detailLink ||
			this.props.isFetching !== nextProps.isFetching ||
			this.props.style !== nextProps.style ||
			this.props.checked !== nextProps.checked
		);
	}

	renderPrice(pricedProduct: PricedProduct<ProductItem>) {
		const { priceType } = this.props;
		const { product, price } = pricedProduct;

		return (
			<Fragment>
				{price ? <span title={price.humanFormatType()}>{price.humanCondensedFormatType()}</span> : null}
				<ProductPriceInfo price={price} priceType={priceType} product={product} />
			</Fragment>
		);
	}

	render() {
		const {
			style,
			index,
			pricedProduct,
			highlightProductId,
			accessory,
			detailLink,
			packageCell,
			isFetching,
			checked,
			checkRow,
		} = this.props;
		const { product } = pricedProduct;
		const detailLinkString = detailLink(product);

		return (
			<div
				className={
					product.status.isRemoved()
						? styles.removedRow
						: product.id === highlightProductId
						? styles.highlightedRow
						: styles.row
				}
				data-productid={product.id}
				data-testid='product-table-row'
				key={product.id}
				style={style}
			>
				{accessory !== undefined ? (
					<div className={styles.addOrRemoveCell}>{accessory(pricedProduct)}</div>
				) : (
					<div className={styles.addOrRemoveCell} data-testid='delete-product'>
						<Checkbox checked={checked} onChange={checkRow} />
					</div>
				)}
				<div className={styles.countryCell}>
					<Link styles={{ button: styles.countryCellContent }} to={detailLinkString}>
						<div className={styles.countryIcon}>
							<CountryIcon code={product.dataset.source.country.alpha2} name={product.dataset.source.country.name} />
						</div>
						{product.dataset.source.country.alpha2}
						<abbr
							className={styles.datasetSourceShortName}
							title={`${product.dataset.source.shortName} — ${product.dataset.source.description}`}
						>
							{product.dataset.source.shortName}
						</abbr>
					</Link>
				</div>
				<div className={styles.brandCell}>
					<Link styles={{ button: styles.brandCellContent }} to={detailLinkString}>
						{product.uppercasedBrandName}
					</Link>
				</div>
				<div className={styles.mahCell}>
					<Link styles={{ button: styles.mahCellContent }} to={detailLinkString}>
						{product.uppercasedMarketingAuthorizationHolder}
					</Link>
				</div>
				<div className={styles.atcCell}>
					<Link styles={{ button: styles.atcCellContent }} to={detailLinkString}>
						{product.atc}
					</Link>
				</div>
				<div className={styles.activeSubstanceCell}>
					<Link styles={{ button: styles.activeSubstanceCellContent }} to={detailLinkString}>
						{product.uppercasedActiveSubstance}
					</Link>
				</div>
				<div className={styles.dosageCell}>
					<Link styles={{ button: styles.dosageCellContent }} to={detailLinkString}>
						{product.uppercasedDosageForm}
					</Link>
				</div>
				<div className={styles.dosageStrengthCell}>
					<Link styles={{ button: styles.dosageStrengthCellContent }} to={detailLinkString}>
						{product.dosageStrength}
					</Link>
				</div>
				<div className={styles.packageCell}>
					{packageCell?.(pricedProduct, index) ?? (
						<Link styles={{ button: styles.packageCellContent }} to={detailLinkString}>
							{product.packageSize}
						</Link>
					)}
				</div>
				<div className={styles.priceCell}>
					<Link styles={{ button: styles.priceCellContent }} to={detailLinkString}>
						<FormattedProductPrice
							rounded
							dataSource={product.dataset.source}
							isFetching={isFetching}
							productPrice={pricedProduct.price}
						/>
					</Link>
				</div>
				<div className={styles.priceTypeCell}>
					<Link styles={{ button: styles.priceTypeCellContent }} to={detailLinkString}>
						{this.renderPrice(pricedProduct)}
					</Link>
				</div>
				<div className={styles.detailCell}>
					<Link to={detailLinkString}>
						<ProductStatusIcon status={product.status} />
					</Link>
				</div>
			</div>
		);
	}
}
